import type { CursorPage } from '../contracts/http';

export type AgentMessageSender = 'user' | 'agent' | 'system';

/** Agent 用于把复杂回复渲染为会话内卡片或展开页的受控 Tool 名称。 */
export const AGENT_PRESENTATION_TOOL_NAME = 'present_reply_artifact';
export const AGENT_REPLY_SUGGESTIONS_TOOL_NAME = 'suggest_next_replies';
export type AgentPresentationMode = 'card' | 'page';

/**
 * 仅属于一条会话消息的展示成果。它不是 Component 或独立应用，不能访问运行时 ctx、
 * 工作台数据或网络；刷新后只恢复本地源码与快照数据。
 */
export interface AgentPresentationArtifactView {
  id: string;
  title: string;
  description: string | null;
  mode: AgentPresentationMode;
  html: string;
  css: string;
  javascript: string;
  data: unknown;
}

export const THINKING_EFFORTS = ['low', 'high', 'max'] as const;
export type ThinkingEffort = (typeof THINKING_EFFORTS)[number];

/** 模型网关支持的 Provider 请求策略。 */
export const MODEL_PROVIDER_KINDS = [
  'openai-compatible',
  'deepseek',
  'moonshot',
  'zhipu',
  'openrouter',
  'claude',
] as const;
export type ModelProviderKind = (typeof MODEL_PROVIDER_KINDS)[number];

export const MODEL_PROVIDER_OPTIONS: Array<{ label: string; value: ModelProviderKind }> = [
  { label: '通用 OpenAI-compatible', value: 'openai-compatible' },
  { label: 'DeepSeek', value: 'deepseek' },
  { label: 'Moonshot / Kimi', value: 'moonshot' },
  { label: '智谱 GLM', value: 'zhipu' },
  { label: 'OpenRouter', value: 'openrouter' },
  { label: 'Claude（原生 Messages）', value: 'claude' },
];

export interface ModelConnectionTestInput {
  desktopDeviceId?: string | null;
  baseUrl: string;
  apiKey: string;
  model: string;
  providerKind?: ModelProviderKind;
  thinkingEnabled?: boolean;
}

export interface ModelConnectionTestResult {
  model: string;
  latencyMs: number;
}

export type AgentMessageAttachmentKind = 'image' | 'document' | 'audio' | 'video';

export const MAX_AGENT_ATTACHMENT_BYTES = 3 * 1024 * 1024;
export const MAX_AGENT_ATTACHMENTS_TOTAL_BYTES = 10 * 1024 * 1024;

export interface AgentMessageAttachmentView {
  id: string;
  name: string;
  kind: AgentMessageAttachmentKind;
  contentType: string;
  size: number;
  textTruncated: boolean;
  /** 文件已从账户文件模块删除；消息仍保留附件名称作为历史记录。 */
  deletedAt: string | null;
}

/** 用户选择的界面元素定位信息；不包含完整 DOM 或样式。 */
export interface ComponentPreviewElementReference {
  tagName: string;
  selector: string;
  text: string;
  attributes: {
    id?: string;
    className?: string;
    role?: string;
    ariaLabel?: string;
    name?: string;
    type?: string;
    testId?: string;
  };
  /** 内置浏览器的页面来源；省略时仍表示组件预览元素。 */
  browser?: { targetId: string; pageUrl: string; frameUrl: string; title: string };
}

export interface AgentMessageView {
  _id: string;
  workspaceId: string;
  sessionId: string;
  runId: string | null;
  sender: AgentMessageSender;
  sourceAgentId?: string | null;
  targetAgentId?: string | null;
  /** pending 表示该用户消息正在等待当前一次 LLM 请求自然结束，尚未进入正式上下文。 */
  deliveryStatus: 'pending' | 'accepted' | 'withdrawn';
  visibleAt: string;
  content: string;
  attachments: AgentMessageAttachmentView[];
  /** 此消息附带的预览元素定位信息；null 表示未选择元素。 */
  previewElement?: ComponentPreviewElementReference | null;
  /** 用户在发送此条消息时选择的深度思考开关；null 表示使用模型档案默认值。 */
  thinkingEnabled: boolean | null;
  /** 用户在发送此条消息时选择的思考强度；null 表示使用默认 high。 */
  thinkingEffort: ThinkingEffort | null;
  thinking: string | null;
  usage: { promptTokens: number; completionTokens: number; totalTokens: number } | null;
  /** 整条回复从开始执行到保存消息的耗时；历史消息可能没有计时数据。 */
  durationMs?: number | null;
  evidence: Array<Record<string, unknown>>;
  /** 从内部 Tool 证据提取的展示成果；源码不再作为普通 Tool 明细暴露。 */
  presentationArtifacts?: AgentPresentationArtifactView[];
  /** 当前 Agent 回复附带的可直接发送文字；历史是否可点击由 suggestedRepliesMessageId 决定。 */
  suggestedReplies?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AgentPendingReplyView {
  sessionId: string;
  runId: string;
  workspaceId: string;
  content: string;
  thinking: string;
  /** 各轮工具调用前的普通正文，独立保留为可展开的执行过程。 */
  progress?: string[];
  toolCalls: Array<{
    id: string;
    name: string;
    status: 'running' | 'completed' | 'failed';
    input?: Record<string, unknown>;
    result?: unknown;
    error?: string;
  }>;
  revision: number;
  startedAt: string;
  updatedAt: string;
}

/** 当前会话下一条正式 LLM 请求的输入上下文占用；与单个 Run 的累计消耗分开统计。 */
export interface AgentContextUsageView {
  promptTokens: number;
  inputBudgetTokens: number;
  thresholdTokens: number;
  targetTokens: number;
  source: 'provider' | 'estimated';
  measuredAt: string | null;
  lastCompression?: {
    beforeTokens: number;
    afterTokens: number;
    reductionPercent: number;
  };
}

export interface AgentSessionHistory {
  messages: AgentMessageView[];
  /** 最新一条尚待用户回复且包含建议的 Agent 消息 ID。 */
  suggestedRepliesMessageId?: string | null;
  pendingReply: AgentPendingReplyView | null;
  /** 已可靠保存、但尚未被当前回复接收的用户插话。 */
  pendingInterjections: AgentMessageView[];
  /** 仅未纳入压缩摘要的已发送用户消息允许作为重新开始锚点。 */
  resettableMessageIds: string[];
  contextUsage: AgentContextUsageView;
}

/** 临时调试用途：当前会话最近一次实际发给模型的文本消息序列。 */
export interface AgentSessionPromptDebugView {
  runId: string;
  capturedAt: string;
  messages: Array<Record<string, unknown>>;
  promptVersion?: string;
  promptHash?: string;
  warnings?: string[];
  contextBudget?: { inputBudgetTokens: number; rolePromptBudgetChars: number };
}

/** 用户停止当前回复时，从待处理插话恢复到本地输入框的内容。 */
export interface AgentRestoredInterjectionView {
  messageId: string;
  content: string;
  previewElement?: ComponentPreviewElementReference | null;
  createdAt: string;
}

export interface AgentConversationStopResult {
  run: AgentRunView;
  restoredInterjections: AgentRestoredInterjectionView[];
}

/** 首条消息启动新会话后的绑定信息；Session 实体仍通过列表与实时事件同步。 */
export interface AgentConversationStartResult {
  session: AgentSessionView;
  runId: string;
  messageId: string;
}

export type AgentSessionOrigin = 'user' | 'automatic';

export interface AgentSessionView {
  _id: string;
  workspaceId: string;
  ownerAgentId: string;
  type: 'primary' | 'delegate' | 'dream' | 'agent';
  /** 创建入口的来源；组件调用和后台执行归入自动会话。 */
  origin: AgentSessionOrigin;
  /** 关联任务的当前调度类型；任务已删除或非任务会话时为空。 */
  taskScheduleType?: 'once' | 'interval' | 'cron' | 'event' | null;
  /** 创建时的客户端；跨端续聊不改变来源，旧会话缺失时按网页版展示。 */
  initiatorClientType?: 'web' | 'desktop' | 'mobile' | null;
  externalChannel?: { provider: import('./connector').ConnectorProvider } | null;
  title: string;
  /** 会话级深度思考偏好；null 表示沿用当前模型档案默认值。 */
  thinkingEnabled: boolean | null;
  /** 会话级思考强度；null 表示使用默认 high。 */
  thinkingEffort: ThinkingEffort | null;
  /** 当前会话的模型覆盖；缺失或空值时沿用 Agent 默认设置。 */
  scope?: { modelPreference?: AgentModelPreference | null };
  /** 当前会话后续 Run 使用的执行环境；每个 Run 创建时解析为不可变快照。 */
  executionTarget: import('./desktop').ExecutionTargetPreference;
  lastActiveAt: string;
  /** 会话的即时运行态由活动 AgentRun 聚合而来，null 表示当前没有执行。 */
  runStatus: 'queued' | 'running' | 'waiting' | 'cancelling' | null;
  compressedContext: { summary: string; originalMessageCount: number } | null;
  contextUsage: Omit<AgentContextUsageView, 'thresholdTokens'> | null;
  /** 仅普通会话具备；梦境会话永远不会参与反思。 */
  reflection?: { reflectedAt: string; title: string; summary: string; memoryIds: string[] } | null;
  /** 仅梦境会话具备，用于展示本次记忆整理覆盖的时间窗口。 */
  dream?: { windowStart: string; windowEnd: string; runKey: string; completedAt?: string } | null;
  communication?: { sourceAgentId: string; targetAgentId: string; parentRunId: string } | null;
}

export type AgentSessionPageView = CursorPage<AgentSessionView>;

export interface AgentView {
  _id: string;
  workspaceId: string;
  kind: 'master' | 'general' | 'component' | 'component_builder';
  componentId: string | null;
  instanceId?: string | null;
  name: string;
  avatar: string | null;
  description: string;
  status: 'active' | 'disabled';
  modelOverrides: { model?: string; temperature?: number; maxTokens?: number };
  toolAuthorization: { packageIds: string[]; toolNames: string[] };
  modelProfileId: string | null;
  modelSource: 'user' | 'system' | null;
  systemPrompt: string;
  memoryPolicy: { autoRecall: boolean; semanticHistoryRecall: boolean; allowSharedMemory: boolean };
  componentDefaults?: {
    customized: boolean;
    updateAvailable: boolean;
    sourceVersionId: string | null;
    starterPrompts?: string[];
  } | null;
}

/** 当前实际使用的模型能力摘要，不包含连接地址或密钥。 */
export interface AgentEffectiveModelView {
  name?: string | null;
  desktopDeviceId?: string | null;
  id: string | null;
  source: 'user' | 'system' | null;
  model: string | null;
  supportsThinking: boolean;
  supportsThinkingEffort: boolean;
  defaultThinkingEnabled: boolean;
  supportsMultimodal: boolean;
}

/** 模型档案选择；空值表示沿用所属入口的默认模型。 */
export interface AgentModelPreference {
  modelProfileId: string | null;
  modelSource: 'user' | 'system' | null;
}
export type BuilderModelPreference = AgentModelPreference;

export interface BuilderModelPreferenceView extends BuilderModelPreference {
  effectiveModel: AgentEffectiveModelView;
}

export interface AgentModelProfileView {
  /** 有值时由绑定电脑访问接口，缺失或 null 时由服务端访问。 */
  desktopDeviceId?: string | null;
  _id: string;
  name: string;
  baseUrl: string;
  model: string;
  apiKey: string;
  enabled: boolean;
  isDefault: boolean;
  hasApiKey: boolean;
  apiKeyDecryptionFailed?: boolean;
  providerKind: ModelProviderKind;
  thinkingEnabled: boolean;
  supportsThinkingEffort: boolean;
  supportsMultimodal: boolean;
  /** 供应商声明的总上下文窗口；未填写时按保守的 8K 处理。 */
  contextWindowTokens?: number;
}
/** 用户可在会话中显式指定的已授权能力。 */
export interface AgentCapabilityMention {
  id: string;
  type: 'skill' | 'mcp';
  name: string;
  description: string;
  reference: string;
}

export interface AgentExtensionView {
  _id: string;
  type: 'skill' | 'mcp';
  name: string;
  description: string;
  endpoint: string | null;
  toolNames: string[];
  agentIds: string[];
  enabled: boolean;
  executionStatus: 'ready' | 'configuration_only';
}
export interface AgentMemoryView {
  _id: string;
  ownerAgentId: string | null;
  visibility: 'private' | 'shared';
  status: 'active' | 'archived';
  title: string;
  content: string;
  tags: string[];
  source: 'user' | 'agent' | 'system';
  canonicalKey: string;
  validationStatus: 'validated' | 'conflicted' | 'pending_review';
  occurrenceCount: number;
  lastConfirmedAt: string;
  relations: Array<{
    memoryId: string;
    type: 'supplements' | 'derived_from' | 'contradicts' | 'supersedes';
    confidence: number;
    createdAt: string;
  }>;
  createdAt: string;
  updatedAt: string;
}
export interface AgentRunView {
  _id: string;
  workspaceId: string;
  agentId: string;
  sessionId: string;
  status:
    | 'queued'
    | 'running'
    | 'waiting'
    | 'cancelling'
    | 'completed'
    | 'failed'
    | 'cancelled'
    | 'interrupted';
  result: string | null;
  error: string | null;
  retryOfRunId: string | null;
  recoveryCount: number;
  cancellationRequestedAt: string | null;
}
export interface AgentMemoryReconciliationView {
  _id: string;
  verdict: 'distinct' | 'duplicate' | 'enriches' | 'contradicts' | 'supersedes' | 'uncertain';
  confidence: number;
  status: 'applied' | 'pending' | 'rolled_back';
  affectedMemoryIds: string[];
  createdAt: string;
}
export interface AgentMemoryClusterView {
  _id: string;
  visibility: 'private' | 'shared';
  label: string;
  summary: string;
  memoryIds: string[];
  updatedAt: string;
}
export interface AgentToolCatalogView {
  name: string;
  packageId: string;
  displayName: string;
  description: string;
  parameters: Array<{
    name: string;
    type: 'string' | 'number' | 'boolean' | 'array' | 'object';
    description: string;
    required?: boolean;
    schema?: Record<string, unknown>;
  }>;
  isBuiltinCore: boolean;
}

export type AgentStreamPayload =
  | {
      kind: 'started';
      sessionId: string;
      runId: string;
      revision: number;
      /** 恢复或步骤续接后的完整流式快照，兼容旧服务未携带此字段。 */
      pendingReply?: AgentPendingReplyView;
    }
  /** 未持久化的模型增量，供已订阅的会话与组件即时渲染；断线后以最终 content 恢复。 */
  | {
      kind: 'content_delta';
      sessionId: string;
      runId: string;
      content: string;
      /** 每次模型请求独立标记，避免工具前的临时正文破坏下一轮 Markdown。 */
      roundId?: string;
    }
  | { kind: 'content'; sessionId: string; runId: string; content: string; revision: number }
  | { kind: 'thinking'; sessionId: string; runId: string; content: string; revision: number }
  | { kind: 'progress'; sessionId: string; runId: string; content: string; revision: number }
  | {
      kind: 'tool';
      sessionId: string;
      runId: string;
      tool: AgentPendingReplyView['toolCalls'][number];
      revision: number;
    }
  | {
      kind: 'context-compressed';
      sessionId: string;
      runId: string;
      reason?: string;
      summary?: string;
      contextUsage: AgentContextUsageView;
    }
  | {
      kind: 'completed';
      sessionId: string;
      runId: string;
      messageId: string | null;
      status: 'completed' | 'failed' | 'cancelled' | 'interrupted';
      errorCode?: string;
      errorDetails?: Record<string, unknown>;
    };
